import { Modal } from "antd";
import { CalendarOutlined, CheckOutlined, CloseCircleFilled, LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { OrderingSeatTarget, SeatableReservation } from "../../../../../api/reservations";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { isTillUnavailable } from "../../../../reservations/hooks/useReservationsEdge";
import { useViewport } from "../../../../../hooks/useViewport";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { GhostButton, ModalFooter, ModalHeader, PrimaryButton, posModalStyles } from "../../modalPrimitives";
import { useOrderingMessage } from "../../useOrderingMessage";
import { canSeatWithBills, orderingSeatBillsOf } from "./orderingSeatBills";
import { SeatChoiceModal } from "./SeatChoiceModal";
import { nextTargetKey, seatOptionKey, targetChoicesOf, type SeatOption } from "./seatOptions";
import { SeatReservationList } from "./SeatReservationList";
import { useSeatModalKeys } from "./useSeatModalKeys";
import { useSeatModalState, type SeatModalInitial } from "./useSeatModalState";
import { useSeatableReservations, useSeatFromOrdering } from "./useSeatReservations";

/**
 * „Настани" от Маркиране — САМО настаняване (без редакция, отказ, нова
 * резервация). Шелът е общият на Маркиране (ModalHeader + ModalFooter с
 * „Отказ" и основния бутон); списъкът е една колона по часове, а избраната
 * резервация се разгъва на място. ↑/↓ избират, Enter натиска основния бутон,
 * Esc — назад/затваря, писането отива в търсачката. „Настани" ВИНАГИ води до
 * въпроса „Настани Иванови?" (SeatChoiceModal, над списъка; seatOptions.ts);
 * при резервация с няколко маси той пита и за сметките — една (по
 * подразбиране) или отделна за всяка маса (orderingSeatBills.ts).
 * Успех → `onSeated(billId)` (родителят избира главната сметка и влиза в менюто).
 */
export const SeatReservationModal = ({
  open,
  onClose,
  onSeated,
  initial = null,
}: {
  open: boolean;
  onClose: () => void;
  onSeated: (billId: number) => void;
  // Гласовото „настани …" с нещо за уточняване: търсенето и резервацията.
  initial?: SeatModalInitial | null;
}) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isNarrow, isDesktop } = useViewport();
  const isEdgeLocal = useIsEdgeLocal();
  const showKeys = isDesktop;
  const query = useSeatableReservations({ enabled: open });
  const seatMutation = useSeatFromOrdering();
  const state = useSeatModalState({ open, data: query.data, initial });
  const { step, selected, seatOptions } = state;
  const busy = seatMutation.isPending;
  const reservations = query.data?.reservations ?? [];
  const choiceReservation =
    step.kind === "choice" ? reservations.find((row) => row.id === step.reservationId) ?? null : null;
  const chosenOption: SeatOption | null =
    step.kind === "choice" ? step.options.find((option) => seatOptionKey(option) === step.selectedKey) ?? null : null;
  // Главната сметка: изборът на сервитьора и при двата вида сметки — при
  // „Отделна…" той решава и на кого са новите маси, затова не се избира сам.
  const target: SeatOption | null = chosenOption;
  // „Сметки" — по пресните данни при всеки рендер; null при една маса.
  const bills =
    choiceReservation && query.data
      ? orderingSeatBillsOf(choiceReservation, query.data, target, isEdgeLocal)
      : null;
  const separateBills = step.kind === "choice" && step.separateBills && bills !== null;

  const seat = async (reservation: SeatableReservation, target: OrderingSeatTarget, separate: boolean) => {
    try {
      const result = await seatMutation.mutateAsync({ id: reservation.id, target, separateBills: separate });
      message.success(
        separate
          ? t("seatReservation.successSeparate", { guest: reservation.guestName, tables: result.tableNames.join(", ") })
          : t("seatReservation.success", { guest: reservation.guestName, table: result.tableNames[0] ?? "" }),
      );
      onClose();
      if (result.billId !== null) onSeated(result.billId);
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, t("common.error")));
      state.setStep({ kind: "list" });
    }
  };

  const primary = () => {
    if (busy) return;
    if (step.kind === "choice") {
      if (!choiceReservation || !target || !canSeatWithBills(target, bills, separateBills)) return;
      void seat(
        choiceReservation,
        target.kind === "new" ? { newBill: true } : { billId: target.bill.billId },
        separateBills,
      );
      return;
    }
    if (!selected || seatOptions?.kind !== "choose") return;
    const { options } = seatOptions;
    state.setStep({
      kind: "choice",
      reservationId: selected.id,
      options,
      selectedKey: options.length === 1 ? seatOptionKey(options[0]) : null,
      separateBills: false,
    });
  };

  /** „Една сметка" / „Отделна сметка за всяка маса" (спряната „Отделна…" не се избира). */
  const chooseBills = (separate: boolean) => {
    if (busy || step.kind !== "choice" || !bills) return;
    if (separate && bills.separateHint !== null) return;
    state.setStep({ ...step, separateBills: separate });
  };

  /** ↑/↓ — във въпроса по редовете „Към коя сметка" (ако ги има), иначе по списъка. */
  const move = (delta: 1 | -1) => {
    if (busy) return;
    if (step.kind === "choice") {
      const keys = targetChoicesOf(step.options).map(seatOptionKey);
      if (keys.length === 0) return;
      state.setStep({ ...step, selectedKey: nextTargetKey(keys, target ? seatOptionKey(target) : null, delta) });
      return;
    }
    state.move(delta);
  };

  const back = () => {
    // Докато настаняването върви, въпросът остава (със спинера).
    if (busy) return;
    if (step.kind === "choice") state.setStep({ kind: "list" });
    else onClose();
  };

  useSeatModalKeys({
    open,
    typing: step.kind === "list",
    inputRef: state.inputRef,
    onEscape: back,
    onEnter: primary,
    onMove: move,
    onSide: (delta) => chooseBills(delta === 1),
    onType: (character) => state.setQuery((previous) => previous + character),
    onClearQuery: () => state.setQuery(""),
  });

  const blocked = seatOptions?.kind === "blocked" ? seatOptions.hint : null;
  // Спряна → изключеният бутон казва защо („Може да се настани след 14:30").
  // На телефон причината не се събира на един ред — по-дребно, до два реда.
  const seatLabel = blocked ? (
    <span className={isNarrow ? "whitespace-normal text-[13px] leading-[1.25]" : ""}>{t(blocked.key, blocked.params)}</span>
  ) : (
    t("seatReservation.seat")
  );

  const totalGuests = reservations.reduce((sum, row) => sum + row.guests, 0);
  const subtitle = [
    t("reservations.form.today"),
    t("reservations.counts.reservations", { count: reservations.length }),
    t("reservations.counts.guests", { count: totalGuests }),
  ].join(" · ");

  const search = (
    <div className={`border-b border-pos-line2 ${isNarrow ? "px-4 pb-3" : "px-[22px] pb-3.5"}`}>
      <label className="flex h-11 items-center gap-2.5 rounded-[10px] border border-pos-line3 bg-pos-panel px-3.5">
        <SearchOutlined className="text-[15px] text-pos-dim" />
        <input
          ref={state.inputRef}
          value={state.query}
          onChange={(event) => state.setQuery(event.target.value)}
          placeholder={t("reservations.list.search")}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-medium text-pos-ink outline-none placeholder:text-pos-muted md:text-sm"
        />
        {state.query && (
          <button
            type="button"
            aria-label={t("reservations.list.clearSearch")}
            onClick={() => state.setQuery("")}
            className="grid h-6 w-6 cursor-pointer place-items-center border-0 bg-transparent p-0 text-[15px] text-pos-muted"
          >
            <CloseCircleFilled />
          </button>
        )}
      </label>
    </div>
  );

  const showSearch = reservations.length > 0;
  const header = (
    <div className="shrink-0">
      <ModalHeader
        icon={<CalendarOutlined />}
        title={t("seatReservation.title")}
        subtitle={subtitle}
        onClose={onClose}
        isNarrow={isNarrow}
        noDivider={showSearch}
      />
      {showSearch && search}
    </div>
  );

  const hasRows = reservations.length > 0;
  const ready = hasRows && !query.isLoading && !query.isError;
  const body = query.isLoading ? (
    <Center>
      <LoadingOutlined spin className="text-[32px] text-pos-accent" />
    </Center>
  ) : query.isError ? (
    <Center>
      {/* 503 от касата (изгледът ѝ е стар) — „без интернет", не „грешка". */}
      <span className="text-pos-danger-ink">
        {isEdgeLocal && isTillUnavailable(query.error) ? t("edge.unavailableOffline") : t("seatReservation.loadError")}
      </span>
      <button
        type="button"
        onClick={() => void query.refetch()}
        className="mt-3 h-10 cursor-pointer rounded-[10px] border border-pos-ctl bg-transparent px-4 text-sm font-semibold text-pos-ink2"
      >
        {t("seatReservation.retry")}
      </button>
    </Center>
  ) : !hasRows ? (
    <Center>
      <span className="mb-3 grid h-14 w-14 place-items-center rounded-[14px] bg-pos-ready-bg text-[24px] text-pos-accent">
        <CalendarOutlined />
      </span>
      <span className="text-[15px] font-extrabold text-pos-ink">{t("reservations.list.empty.upcoming")}</span>
      <span className="mt-1 text-[13px] text-pos-soft2">{t("seatReservation.emptyHint")}</span>
    </Center>
  ) : (
    // flex-auto (не flex-1): модалът е с max-h и се свива при малко резервации.
    <div className={`min-h-0 flex-auto overflow-y-auto overscroll-contain ${isNarrow ? "px-3 pb-3" : "px-[22px] pb-3"}`}>
      <SeatReservationList
        groups={state.groups}
        selectedId={selected?.id ?? null}
        nowMs={state.nowMs}
        isNarrow={isNarrow}
        onSelect={state.select}
      />
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      keyboard={false}
      destroyOnHidden
      width={isNarrow ? "100%" : 640}
      styles={posModalStyles}
      style={{ maxWidth: isNarrow ? "calc(100vw - 16px)" : "calc(100vw - 32px)" }}
    >
      <div
        className={`flex flex-col bg-pos-card text-pos-ink ${
          isNarrow ? "max-h-[calc(100dvh-16px)]" : "max-h-[min(760px,90vh)]"
        }`}
      >
        {header}
        {body}
        <div className="shrink-0">
          <ModalFooter isNarrow={isNarrow} columns={ready ? "1fr 2fr" : undefined}>
            {/* Масив, не фрагмент: ModalFooter брои децата си през Children.toArray. */}
            {[
              <GhostButton
                key="back"
                label={t("common.cancel")}
                onClick={onClose}
                shortcut={showKeys ? "Esc" : undefined}
              />,
              ready && (
                <PrimaryButton
                  key="seat"
                  label={seatLabel}
                  icon={blocked ? undefined : <CheckOutlined />}
                  onClick={primary}
                  disabled={busy || !selected || blocked !== null}
                  shortcut={showKeys ? "⏎" : undefined}
                />
              ),
            ]}
          </ModalFooter>
        </div>
      </div>
      <SeatChoiceModal
        reservation={choiceReservation}
        options={step.kind === "choice" ? step.options : []}
        target={target}
        bills={bills}
        separateBills={separateBills}
        busy={busy}
        onSelect={(key) => step.kind === "choice" && state.setStep({ ...step, selectedKey: key })}
        onSeparateChange={chooseBills}
        onBack={back}
        onConfirm={primary}
      />
    </Modal>
  );
};

const Center = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-[260px] flex-auto flex-col items-center justify-center px-6 text-center text-[13px] text-pos-muted">
    {children}
  </div>
);
